import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Flex, Image, useBreakpointValue } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperRef } from 'swiper/react';
import { AssetType } from '../../../../components/photo-grid/utils/types';
import { useNavigateTo } from '../../../../hooks';
import { useBuildURL } from '../../../../hooks/useBuildURL';
import { Text } from '../../../../ui-components';
import { IconButton } from '../../../../ui-components/button/icon-button';
import { CarouselWrapper } from '../../../../ui-components/carousel/CarouselWrapper';
import { getGiftCardDesignName } from '../../../../utils/product-utils';
import type { Product } from '../../../../bff-api/products/model';
import type { SwiperMethods } from '../../../../ui-components/carousel/types';

interface DesignSelectorProps {
    product?: Product;
}

export const DesignSelector: FC<DesignSelectorProps> = ({ product }) => {
    const { buildPdpURL } = useBuildURL();
    const navigateTo = useNavigateTo();
    const ref = useRef<SwiperRef>(null);

    const assets = useMemo(
        () =>
            product?.c_variants
                ?.filter((variant) => !variant.isOutOfStock)
                .map((variant) => {
                    const designName = product.variationAttributes?.[0].values.find(
                        (attribute) => attribute.value === variant.id,
                    )?.name;

                    return {
                        id: variant.id,
                        assetUrl: variant.c_imageUrl,
                        assetType: AssetType.IMAGE,
                        title: designName,
                        alt: designName,
                    };
                }),
        [product?.c_variants, product?.variationAttributes],
    );

    const minAssetsForArrows = useBreakpointValue({ base: 1, md: 4 });

    const shouldShowArrows = useMemo(() => {
        const assetsLength = assets?.length ?? 0;
        return assetsLength >= (minAssetsForArrows ?? 1);
    }, [assets?.length, minAssetsForArrows]);

    const getSwiper = useCallback(
        (): SwiperMethods | undefined => ref.current?.swiper as SwiperMethods | undefined,
        [],
    );

    const findSelectedDesignIndex = useCallback(
        (designId?: string) => assets?.findIndex((asset) => asset.id === (designId || product?.id)),
        [assets, product?.id],
    );

    const scrollToSelectedDesign = useCallback(
        (designId?: string) => {
            const swiper = getSwiper();
            if (swiper) {
                const selectedIndex = findSelectedDesignIndex(designId);
                swiper.slideTo(selectedIndex ?? 0);
            }
        },
        [findSelectedDesignIndex, getSwiper],
    );

    useEffect(() => {
        if (product?.id) {
            scrollToSelectedDesign(product.id);
        }
    }, [product?.id, scrollToSelectedDesign]);

    const handleSwiperInit = useCallback(() => {
        scrollToSelectedDesign();
    }, [scrollToSelectedDesign]);

    const onClickSlide = (designId: string): void => {
        if (product && product.id !== designId) {
            navigateTo(buildPdpURL(product, designId));
            scrollToSelectedDesign(designId);
        }
    };

    const handleNavigateNext = useCallback((): void => {
        const swiper = getSwiper();
        if (swiper) {
            swiper.slideNext();
        }
    }, [getSwiper]);

    const handleNavigatePrev = useCallback((): void => {
        const swiper = getSwiper();
        if (swiper) {
            swiper.slidePrev();
        }
    }, [getSwiper]);

    return (
        <Flex
            gap={4}
            direction="column"
            data-testid="design-selector"
            className="max-w-full overflow-hidden [&_.swiper-button-next]:!hidden [&_.swiper-button-prev]:!hidden [&_.swiper-wrapper]:flex [&_.swiper-wrapper]:gap-0 [&_.swiper]:w-full [&_.swiper]:max-w-full [&_.swiper]:overflow-hidden"
        >
            <Flex justifyContent="space-between" gap={2} alignItems="end">
                <Flex gap={1} flexWrap="wrap">
                    <Text style="bold">
                        <FormattedMessage
                            id="gift_card.select_design"
                            defaultMessage="Select a Design"
                        />
                    </Text>
                    <Text>
                        <FormattedMessage
                            id="gift_card.select_design_name"
                            defaultMessage="{designName}"
                            values={{ designName: product ? getGiftCardDesignName(product) : '' }}
                        />
                    </Text>
                </Flex>
                {shouldShowArrows && (
                    <Flex gap={2} marginTop="3px" marginRight="3px">
                        <IconButton
                            onClick={handleNavigatePrev}
                            size="md"
                            id="slider-navigation-left"
                            iconName="navigation-arrow-left"
                        />
                        <IconButton
                            onClick={handleNavigateNext}
                            size="md"
                            id="slider-navigation-right"
                            iconName="navigation-arrow-right"
                        />
                    </Flex>
                )}
            </Flex>
            <CarouselWrapper>
                <Swiper
                    id="test"
                    ref={ref}
                    slidesPerView="auto"
                    slidesPerGroup={1}
                    spaceBetween={16}
                    rewind
                    style={{ width: '100%', paddingRight: '3px' }}
                    modules={[Navigation]}
                    lazyPreloadPrevNext={2}
                    navigation
                    onInit={handleSwiperInit}
                >
                    {assets?.map((asset) => (
                        <SwiperSlide
                            key={asset.id}
                            className="box-border !w-[200px] !max-w-[200px] !flex-[0_0_200px] !p-0"
                        >
                            <button
                                className="relative block w-full"
                                type="button"
                                onClick={() => onClickSlide(asset.id)}
                                data-testid={`gift-card-design-option-${asset.id}`}
                            >
                                <Image
                                    data-testid={asset.id}
                                    src={asset.assetUrl}
                                    alt={asset.alt}
                                    width="200px"
                                    height="132px"
                                    objectFit="cover"
                                    borderRadius="16px"
                                    borderWidth="1px"
                                    borderStyle="solid"
                                    borderColor={
                                        product?.id === asset.id
                                            ? 'slt-pepper.1000'
                                            : 'slt-pepper.300'
                                    }
                                    boxSizing="content-box"
                                />
                            </button>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </CarouselWrapper>
        </Flex>
    );
};

DesignSelector.defaultProps = {
    product: undefined,
};
